import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { OwnedAccount } from '@hotjar/account';
import type {
  AnalyticsProduct,
  FeedbackProduct,
  ProductCatalog,
} from '@hotjar/webapp/domains/common/product/types';
import type { Site } from '@hotjar/site';
import { isPlanDowngradePeriod, isPlanLocked } from '@hotjar/site';
import { useProductAllowances } from '@hotjar/webapp/domains/revenue/hooks/usePlanAllowances';
import { MultiSkuDropdown } from '@hotjar/webapp/features/Billing/MultiSkuDropdown/MultiSkuDropdown';
import { SiteLockedIcon } from '@hotjar/webapp/features/Billing/SiteBox/SiteLockedIcon';
import { useTranslationWithPrefix } from '@hotjar/translations';
import {
  useGetSiteEffectiveProducts,
  useGetSiteProducts,
} from '@hotjar/webapp/domains/common/product/hooks/useGetSiteProducts';
import { checkout } from '@hotjar/checkout';
import { useReadableProductName } from '@hotjar/webapp/domains/common/product/hooks/useReadableProductName';
import { PlanBadge, Stack, tokens, useNotify, Link, Badge } from '@hotjar/hotsauce';
import { PaymentData, SitePlanInfo } from '@hotjar/webapp/billing/plan';
import { MultiSkuDowngrade } from './MultiSkuDowngrade';
import { ProductTitle } from '../../MultiSkuDropdown/styles';

interface Props {
  account: OwnedAccount;
  site: Site;
  onPlanChange: (siteId: number, plan: FeedbackProduct | AnalyticsProduct) => void;
  activeAnalyticsProductSku: string | undefined;
  activeFeedbackProductSku: string | undefined;
  productCatalog: ProductCatalog;
}

const DataCollectionRateContainer = styled.div`
  float: left;
  margin-right: 5%;
  position: relative;
  width: 54%;
`;

const SiteLockedContainer = styled.div`
  padding-top: 10px;
  white-space: nowrap;
`;

const PlanBadgeAlignedToRight = styled(PlanBadge)`
  margin-right: ${tokens.spacer.x1};
`;

const Container = styled(Stack)`
  padding-top: ${tokens.spacer.x4};
`;

const ActionRequiredBadge = styled(Badge)`
  background-color: ${tokens.BRAND_COLORS.neutral_900};
`;

export const MultiSkuCollectionRates = ({
  account,
  site,
  onPlanChange,
  activeAnalyticsProductSku,
  activeFeedbackProductSku,
  productCatalog,
}: Props): JSX.Element | null => {
  const { analyticsProduct, feedbackProduct } = useGetSiteProducts(site);
  const { effectiveAnalyticsProduct, effectiveFeedbackProduct } = useGetSiteEffectiveProducts(site);
  const planDowngradePeriod = isPlanDowngradePeriod(site);
  const { showToast } = useNotify();
  const { t } = useTranslationWithPrefix('billing:multi-sku-site-box');
  const { t: tRevenue } = useTranslationWithPrefix('revenue:migrate-to-two-skus');

  const siteLocked = isPlanLocked(site, account);
  const { getAllowances } = useProductAllowances();
  const defaultSiteState = !siteLocked && !planDowngradePeriod;
  const analyticsProductName = useReadableProductName(analyticsProduct?.type);
  const feedbackProductName = useReadableProductName(feedbackProduct?.type);

  if (!analyticsProduct || !feedbackProduct) {
    return null;
  }

  if (defaultSiteState)
    return (
      <DataCollectionRateContainer>
        <MultiSkuDropdown
          siteAccount={site.account}
          activeFeedbackProductSku={activeFeedbackProductSku}
          activeAnalyticsProductSku={activeAnalyticsProductSku}
          productCatalog={productCatalog}
          onChange={(selectedProduct: FeedbackProduct | AnalyticsProduct) =>
            onPlanChange(site.id, selectedProduct)
          }
          isTwoSkusActionRequired={site.isTwoSkusActionRequired}
        />
      </DataCollectionRateContainer>
    );

  if (planDowngradePeriod && effectiveFeedbackProduct && effectiveAnalyticsProduct) {
    const observeIsDowngrading =
      analyticsProduct.dailyCcSessions < effectiveAnalyticsProduct.dailyCcSessions;
    const askIsDowngrading =
      feedbackProduct.monthlyResponses < effectiveFeedbackProduct.monthlyResponses;
    const costSpecs = [new SitePlanInfo(site.id, site.effectivePlanId)];
    const paymentData = new PaymentData(costSpecs, undefined, undefined, account.billingCycle);
    const numberOfDowngrades = [observeIsDowngrading, askIsDowngrading].filter(Boolean).length;

    const cancelDowngrade = async () => {
      try {
        await checkout({ account, paymentData });
        window.location.reload();
      } catch {
        showToast({
          kind: 'danger',
          text: t('cancel-downgrade-error'),
        });
      }
    };

    return (
      <DataCollectionRateContainer>
        <Container
          direction="column"
          spacing="x2pt5"
          css={css`
            padding-top: ${tokens.spacer.x1pt5};
            align-items: flex-start;
          `}
        >
          <div>
            <ProductTitle>{analyticsProductName}</ProductTitle>
            {observeIsDowngrading ? (
              <MultiSkuDowngrade
                site={site}
                effectiveAllowance={effectiveAnalyticsProduct.dailyCcSessions}
                futureAllowance={analyticsProduct.dailyCcSessions}
                effectiveFeatureGroup={effectiveAnalyticsProduct.tier}
                futureFeatureGroup={analyticsProduct.tier}
              />
            ) : (
              <SiteLockedContainer data-testid="site-locked">
                <PlanBadgeAlignedToRight kind={analyticsProduct.tier} size="m" />
                {getAllowances(analyticsProduct).allowanceLabel}
                <SiteLockedIcon text={t('cannot-change-in-fair-downgrade')} />
              </SiteLockedContainer>
            )}
          </div>
          <div>
            <ProductTitle>{feedbackProductName}</ProductTitle>
            {askIsDowngrading ? (
              <MultiSkuDowngrade
                site={site}
                effectiveAllowance={effectiveFeedbackProduct.monthlyResponses}
                futureAllowance={feedbackProduct.monthlyResponses}
                effectiveFeatureGroup={effectiveFeedbackProduct.tier}
                futureFeatureGroup={feedbackProduct.tier}
              />
            ) : (
              <SiteLockedContainer data-testid="site-locked">
                <PlanBadgeAlignedToRight kind={feedbackProduct.tier} size="m" />
                {getAllowances(feedbackProduct).allowanceLabel}
                <SiteLockedIcon text={t('cannot-change-in-fair-downgrade')} />
              </SiteLockedContainer>
            )}
          </div>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            as="button"
            kind="standalone"
            onClick={cancelDowngrade}
            data-testid="cancel-downgrade"
          >
            {t(`cancel-downgrades`, { count: numberOfDowngrades })}
          </Link>
        </Container>
      </DataCollectionRateContainer>
    );
  }

  if (siteLocked) {
    return (
      <DataCollectionRateContainer>
        <Container
          direction="column"
          spacing="x5"
          css={css`
            padding-top: 0;
            gap: 30px;
          `}
        >
          <div>
            <ProductTitle>{analyticsProductName}</ProductTitle>
            <SiteLockedContainer data-testid="site-locked">
              <PlanBadgeAlignedToRight kind={analyticsProduct.tier} size="m" />
              {getAllowances(analyticsProduct).allowanceLabel}
              <SiteLockedIcon />
            </SiteLockedContainer>
          </div>

          <div>
            <ProductTitle>{feedbackProductName}</ProductTitle>
            <SiteLockedContainer data-testid="site-locked">
              {site.isTwoSkusActionRequired && (
                <ActionRequiredBadge size="m" kind="custom">
                  {tRevenue('action-required')}
                </ActionRequiredBadge>
              )}
              {!site.isTwoSkusActionRequired && (
                <>
                  {' '}
                  <PlanBadgeAlignedToRight kind={feedbackProduct.tier} size="m" />
                  {getAllowances(feedbackProduct).allowanceLabel}
                </>
              )}

              <SiteLockedIcon />
            </SiteLockedContainer>
          </div>
        </Container>
      </DataCollectionRateContainer>
    );
  }

  return null;
};
